import { useOptionalRef } from '@shared/hooks';
import { makeRootClassName, StyleProps } from '@shared/utils';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, ReactElement, useState } from 'react';
import { LuCheck, LuCopy } from 'react-icons/lu';
import { Button, ButtonProps } from '..';

// NOTE(parlato): Fallback method when navigator clipboard
// API is unavailable
const copyElementText = (textToCopy: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = textToCopy;
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();

  document.execCommand('copy');

  document.body.removeChild(textArea);
};

export type CopyButtonProps = StyleProps &
  Omit<ButtonProps, 'children' | 'onPress' | 'icon'> & {
    textToCopy: string;
    label?: string;
    onCopy?: () => void;
    icon?: ReactElement | null;
  };

const ROOT = makeRootClassName('CopyButton');

const DEFAULT_PROPS = {} as const;

export const handleCopySafe = async (textToCopy: string) => {
  try {
    await navigator.clipboard.writeText(textToCopy);
  } catch {
    copyElementText(textToCopy);
  }
};

function CopyButton(
  props: CopyButtonProps,
  ref: ForwardedRef<HTMLDivElement>
): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const domRef = useOptionalRef(ref);

  const { textToCopy, label, onCopy, ...restButtonProps } = p;
  const [recentlyPressed, setRecentlyPressed] = useState(false);

  const handleCopy = async () => {
    await handleCopySafe(textToCopy);
    onCopy && onCopy();
    setRecentlyPressed(true);
    setTimeout(() => {
      setRecentlyPressed(false);
    }, 1000);
  };

  const icon = decideIcon(recentlyPressed, p.icon);

  return (
    <div ref={domRef} className={clsx(ROOT, p.className)}>
      <Button
        tooltip="Copy to clipboard"
        {...restButtonProps}
        icon={icon}
        onPress={handleCopy}
      >
        {label}
      </Button>
    </div>
  );
}

function decideIcon(
  recentlyPressed: boolean,
  icon?: ReactElement | null
): ReactElement | null {
  if (recentlyPressed) return <LuCheck />;
  if (icon === null) return icon;
  if (icon === undefined) return <LuCopy />;
  return icon;
}

export const CopyButtonForwardRef = forwardRef<HTMLDivElement, CopyButtonProps>(
  CopyButton
);

export default CopyButtonForwardRef;
